import { PLATFORM_TIERS, ROLES } from '@/constants/roles';

/**
 * What each platform staff tier may do, in the browser — the client-side mirror of the backend's
 * `PlatformPolicy` (ADR-0032 §3, RBAC §6).
 *
 * **This is presentation, never enforcement.** Every predicate here exists so the console shows a
 * person the surfaces they can actually use, instead of a nav full of links that return 403. The
 * authorization decision is the backend's and is re-made on every request; if these two ever
 * disagree, the backend wins and the user sees a refusal — which is the correct failure direction.
 * Nothing here may become the only thing standing between a caller and a capability.
 *
 * Kept as one module rather than inlined at each call site for the same reason `PlatformPolicy`
 * exists: the previous single `Admin` check could not disagree with itself, and three ordered tiers
 * spread across a router, a nav array and a menu absolutely can.
 *
 * The tier lists below are duplicated from the backend by necessity — there is no generated contract
 * for them. When a predicate changes there, change it here, and check
 * `docs/architecture/RBAC.md` §6 rather than guessing from the name.
 */

const SUPER_ADMIN_ONLY: readonly string[] = [ROLES.Admin];
const SUPER_ADMIN_AND_PLATFORM_ADMIN: readonly string[] = [ROLES.Admin, ROLES.PlatformAdmin];
const ALL_TIERS: readonly string[] = PLATFORM_TIERS;

const has = (roles: readonly string[] | undefined, allowed: readonly string[]): boolean =>
  roles?.some((role) => allowed.includes(role)) ?? false;

/** Any of the three platform tiers — i.e. may the caller open the admin console at all. */
export const isPlatformStaff = (roles: readonly string[] | undefined): boolean =>
  has(roles, ALL_TIERS);

// ── Super Admin only ─────────────────────────────────────────────────────────

/** Grant or revoke a platform tier (RBAC §7-P1). The rule that makes the ladder real. */
export const canGrantPlatformRole = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_ONLY);

/** Read the role catalog and its assignment counts — the surface privilege is governed from. */
export const canManageRoleCatalog = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_ONLY);

/**
 * Read the persisted platform-role audit trail (ADR-0033 §14).
 *
 * **Not inherited from directory access**, which is the mistake worth guarding: Platform Admin and Support
 * both read the user directory the history is shown inside, but the record of how privilege has MOVED belongs
 * to the tier that governs privilege. For a lesser tier it is a map of every recently elevated account.
 */
export const canViewPlatformRoleAudit = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_ONLY);

/**
 * Suspend, delete or restore a tenant: whether a customer exists, not merely how it is administered
 * (RBAC §6, ADR-0032 §4). Deliberately narrower than {@link canManageAnyCompany}, because Platform
 * Admin edits company records but does not decide which companies the platform has.
 */
export const canManageTenantLifecycle = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_ONLY);

// ── Super Admin + Platform Admin ─────────────────────────────────────────────

/** Cross-tenant company, organization, job and registration administration. */
export const canManageAnyCompany = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_AND_PLATFORM_ADMIN);

/** Deactivate, reactivate or lock an account. Support restores access; it never removes it. */
export const canManageUserState = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_AND_PLATFORM_ADMIN);

/**
 * Administer the platform-wide job category taxonomy (RBAC §6).
 *
 * **Not a Support capability**, even though it is content rather than PII: the taxonomy is shared by every
 * tenant, so a change here is visible to all of them at once. Support restores access to accounts; it does
 * not reshape what the whole platform hires under.
 */
export const canManageJobCategories = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_AND_PLATFORM_ADMIN);

/**
 * Read application records across companies — the platform applications grid and one application's
 * read-only detail (ADR-0036).
 *
 * **A read, and only a read.** There is no platform mutation behind it: moving a stage, rejecting,
 * hiring and reaching a resume all still resolve a company membership on the backend and refuse a
 * non-member. A console that offered any of those actions would be lying about what the capability is.
 *
 * **Separate from `canViewPlatformReports`**, which all three tiers hold: that one is the funnel
 * COUNTS, this one is the ROWS behind them. Support may read how many applications sit at each status
 * and not which applications those are, because a row names the candidate — the same line
 * `canViewCandidatePii` draws.
 */
export const canViewPlatformApplications = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_AND_PLATFORM_ADMIN);

/**
 * Candidate profiles, resumes and the candidate-access report.
 *
 * **Excludes Support deliberately** — the recorded cross-tenant PII exception stops at Platform Admin
 * (ADR-0032 §5). Widening this to Support would put a PII link in front of the tier the ladder was
 * built to keep away from it.
 */
export const canViewCandidatePii = (roles: readonly string[] | undefined): boolean =>
  has(roles, SUPER_ADMIN_AND_PLATFORM_ADMIN);

// ── All three tiers ──────────────────────────────────────────────────────────

/** The Platform User Directory and a single user's detail. */
export const canViewUserDirectory = (roles: readonly string[] | undefined): boolean =>
  has(roles, ALL_TIERS);

/** Unlock, reset MFA, resend verification — the support desk's whole job. */
export const canRestoreAccountAccess = (roles: readonly string[] | undefined): boolean =>
  has(roles, ALL_TIERS);

/** Cross-tenant operational aggregates and system health. Counts and status, never records. */
export const canViewPlatformReports = (roles: readonly string[] | undefined): boolean =>
  has(roles, ALL_TIERS);
